import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { twMerge } from 'tailwind-merge';
const sizeClasses = {
    small: 'h-8 text-xs',
    default: 'h-10 text-sm',
    large: 'h-12 text-base',
};
const labelSizeClasses = {
    small: 'text-xs',
    default: 'text-sm',
    large: 'text-base',
};
const textareaSizeClasses = {
    small: 'text-xs py-1.5',
    default: 'text-sm py-2',
    large: 'text-base py-3',
};
/**
 * A form field component designed to work with react-hook-form.
 * Use with {...register('fieldName')} spread directly on the component.
 *
 * @example
 * <FormField
 *   label="Email"
 *   error={errors.email?.message}
 *   {...register('email', { required: 'Email is required' })}
 * />
 *
 * @example
 * // With prefix/suffix
 * <FormField
 *   label="Price"
 *   prefix={<span>$</span>}
 *   suffix={<span>.00</span>}
 *   {...register('price')}
 * />
 *
 * @example
 * // Textarea
 * <FormField
 *   label="Description"
 *   rows={4}
 *   {...register('description')}
 * />
 */
export const FormField = React.forwardRef((props, ref) => {
    const { label, name, error, required, className, placeholder, prefix, suffix, size = 'default', rows, ...restProps } = props;
    const hasError = Boolean(error);
    const hasAdornment = Boolean(prefix || suffix);
    const isTextarea = typeof rows === 'number';
    const baseInputClasses = twMerge('w-full px-3 border rounded-md transition-colors', 'focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent', hasError
        ? 'border-red-500 focus:ring-red-500'
        : 'border-gray-300 hover:border-gray-400', 'disabled:bg-gray-100 disabled:cursor-not-allowed');
    const inputElement = isTextarea ? (_jsx("textarea", { ref: ref, id: name, name: name, rows: rows, placeholder: placeholder, className: twMerge(baseInputClasses, textareaSizeClasses[size], 'resize-y min-h-[80px]'), "aria-invalid": hasError, "aria-describedby": hasError ? `${name}-error` : undefined, ...restProps })) : (_jsx("input", { ref: ref, id: name, name: name, type: restProps.type || 'text', placeholder: placeholder, className: twMerge(baseInputClasses, sizeClasses[size], hasAdornment && 'rounded-none border-0 focus:ring-0'), "aria-invalid": hasError, "aria-describedby": hasError ? `${name}-error` : undefined, ...restProps }));
    return (_jsxs("div", { className: twMerge('mb-4', className), children: [label && (_jsxs("label", { htmlFor: name, className: twMerge('block font-medium text-gray-700 mb-1', labelSizeClasses[size]), children: [label, required && _jsx("span", { className: "text-red-500 ml-1", children: "*" })] })), hasAdornment && !isTextarea ? (_jsxs("div", { className: twMerge('flex items-center border rounded-md overflow-hidden transition-colors', hasError
                    ? 'border-red-500 focus-within:ring-2 focus-within:ring-red-500'
                    : 'border-gray-300 hover:border-gray-400 focus-within:ring-2 focus-within:ring-primary focus-within:border-transparent', sizeClasses[size]), children: [prefix && (_jsx("span", { className: "flex items-center px-3 bg-gray-50 text-gray-500 border-r border-gray-300 h-full", children: prefix })), inputElement, suffix && (_jsx("span", { className: "flex items-center px-3 bg-gray-50 text-gray-500 border-l border-gray-300 h-full", children: suffix }))] })) : (inputElement), error && (_jsx("p", { id: `${name}-error`, className: "mt-1 text-sm text-red-500", role: "alert", children: error }))] }));
});
FormField.displayName = 'FormField';
//# sourceMappingURL=FormField.js.map